import React, { useCallback, useEffect, useState } from 'react';
import useCheckoutRedirect from '../../../hooks/useCheckoutRedirect';
import { pushEvent, ANALYTICS_EVENTS } from '../../../utils/analytics';
import useUser from '../../../hooks/useUser';
import type { SubscriptionRecord } from '../../../types/account';
import { getErrorMessage } from '../../../utils/errorMessage';
import { Alert, Button, Spinner } from '../../../design-system';
import {
  getAccountSettingsUserCacheKey,
  makeAccountSettingsCacheKey,
  readAccountSettingsCache,
  readThroughAccountSettingsCache,
} from '../accountSettingsDataCache';
import './PaymentMethodManagement.css';

interface PaymentCard {
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
  funding?: string | null;
}

interface PaymentMethodRecord {
  id: string;
  card?: PaymentCard | null;
}

interface PaymentMethodsResponse {
  paymentMethods?: PaymentMethodRecord[];
  defaultPaymentMethod?: PaymentMethodRecord | null;
  message?: string;
}

interface PaymentMethodCacheData {
  defaultPaymentMethod: PaymentMethodRecord | null;
}

interface PaymentMethodManagementProps {
  subscription: Partial<SubscriptionRecord> | null;
}

const PaymentMethodManagement = ({ subscription }: PaymentMethodManagementProps) => {
  const { getApiToken, user } = useUser();
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'api';
  const paymentMethodCacheKey = makeAccountSettingsCacheKey(
    'payment-methods',
    getAccountSettingsUserCacheKey(user),
    subscription?.id || null,
    apiBaseUrl,
  );
  const cachedPaymentMethod = readAccountSettingsCache<PaymentMethodCacheData>(paymentMethodCacheKey);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentMethodRecord | null>(
    () => cachedPaymentMethod?.defaultPaymentMethod || null,
  );
  const [loading, setLoading] = useState(() => Boolean(subscription?.id) && !cachedPaymentMethod);
  const [error, setError] = useState('');
  const { startCheckout: startPaymentUpdate } = useCheckoutRedirect('account_payment_update' as never);

  const fetchPaymentMethods = useCallback(async (options: { force?: boolean } = {}) => {
    if (!subscription?.id) return;
    const cached = readAccountSettingsCache<PaymentMethodCacheData>(paymentMethodCacheKey);
    if (cached && !options.force) {
      setDefaultPaymentMethod(cached.defaultPaymentMethod);
      setError('');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const data = await readThroughAccountSettingsCache<PaymentMethodCacheData>(paymentMethodCacheKey, async () => {
        const token = await getApiToken();
        const response = await fetch(`${apiBaseUrl}/subscription-management/payment-methods`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 404) {
          return { defaultPaymentMethod: null };
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({})) as Partial<PaymentMethodsResponse>;
          throw new Error(errorData.message || `Failed to load payment methods: ${response.status}`);
        }

        const data = await response.json() as PaymentMethodsResponse;

        const validPaymentMethods = (data.paymentMethods || []).filter((pm): pm is PaymentMethodRecord =>
          Boolean(pm?.id && pm.card)
        );
        const validDefaultPaymentMethod = data.defaultPaymentMethod?.id && data.defaultPaymentMethod.card
          ? data.defaultPaymentMethod
          : validPaymentMethods[0] || null;

        pushEvent({
          event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
          action: 'payment_methods_viewed',
          subscription_id: subscription?.id,
          payment_methods_count: validPaymentMethods.length,
          raw_payment_methods_count: data.paymentMethods?.length || 0
        });

        return { defaultPaymentMethod: validDefaultPaymentMethod };
      }, options);

      setDefaultPaymentMethod(data.defaultPaymentMethod);
    } catch (err) {
      const message = getErrorMessage(err, '');
      if (message.includes('login_required')) {
        setError('Please log in again to view payment methods.');
      } else if (message.includes('consent_required')) {
        setError('Additional authorization required. Please log in again.');
      } else if (message && !/network|failed to fetch/i.test(message)) {
        setError(message);
      } else {
        setError('Unable to load payment methods. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, getApiToken, paymentMethodCacheKey, subscription?.id]);

  useEffect(() => {
    if (subscription?.id) {
      void fetchPaymentMethods();
      return;
    }

    setLoading(false);
    setError('');
    setDefaultPaymentMethod(null);
  }, [fetchPaymentMethods, subscription?.id]);

  const handleUpdatePaymentMethod = async () => {
    const targetSlug = subscription?.productSlug || 'pro-plan';
    pushEvent({
      event: ANALYTICS_EVENTS.SUBSCRIPTION_MANAGEMENT_VIEWED,
      action: 'payment_method_update_initiated',
      subscription_id: subscription?.id,
      current_payment_method: defaultPaymentMethod?.id,
      product_slug: targetSlug,
    });

    try {
      await startPaymentUpdate({
        products: [{ productSlug: targetSlug }],
        flowContext: 'account_payment_update',
        contact: null,
        metadata: {
          origin: {
            flow: 'account_payment_update',
            subscriptionId: subscription?.id,
          },
        },
      });
    } catch {
      setError('Unable to open the checkout experience. Please try again.');
    }
  };

  const formatCardBrand = (brand = 'card'): string => {
    const brandNames = {
      'visa': 'Visa',
      'mastercard': 'Mastercard',
      'amex': 'American Express',
      'discover': 'Discover',
      'diners': 'Diners Club',
      'jcb': 'JCB',
      'unionpay': 'UnionPay'
    } as const;
    if (brand in brandNames) {
      return brandNames[brand as keyof typeof brandNames];
    }
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  if (loading) {
    return (
      <section className="payment-method-section">
        <header className="payment-method-section-header">Payment Method</header>
        <div className="payment-method-loading account-settings-loading-state account-settings-loading-state--inline">
          <Spinner label="Loading payment methods" />
          <span>Loading payment methods...</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="payment-method-section">
        <header className="payment-method-section-header">Payment Method</header>
        <Alert variant="danger" className="payment-method-error">
          <span>{error}</span>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => fetchPaymentMethods({ force: true })}
          >
            Retry
          </Button>
        </Alert>
      </section>
    );
  }

  return (
    <section className="payment-method-section">
      <header className="payment-method-section-header">Payment Method</header>
      {defaultPaymentMethod ? (
        <div className="payment-method-details">
          <div className="current-payment-method">
            <div className="payment-method-info">
              <div className="payment-method-text">
                <div className="payment-method-main">
                  {formatCardBrand(defaultPaymentMethod.card?.brand || 'card')}
                  <span className="card-last4"> •••• {defaultPaymentMethod.card?.last4}</span>
                  <span className="default-payment-label">Default</span>
                </div>
                <div className="payment-method-secondary">
                  Expires {defaultPaymentMethod.card?.exp_month}/{defaultPaymentMethod.card?.exp_year}
                  {defaultPaymentMethod.card?.funding && (
                    <span className="card-funding"> • {defaultPaymentMethod.card.funding.charAt(0).toUpperCase() + defaultPaymentMethod.card.funding.slice(1)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="payment-method-actions">
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpdatePaymentMethod}
              className="update-payment-method-btn"
            >
              Update Payment Method
            </Button>
          </div>
        </div>
      ) : (
        <div className="no-payment-method">
          <div>
            <p className="no-payment-copy">No payment method on file</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleUpdatePaymentMethod}
              className="add-payment-method-btn"
            >
              Add Payment Method
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default PaymentMethodManagement;
